import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ClipboardList, Users, BarChart2, Ship, ArrowRight,
  BookOpen, FolderOpen, Shield, GraduationCap,
  ClipboardCheck, AlertOctagon, Brain,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import { supabase } from '@/services/db/supabase';
import { getTodayPublishedManifest } from '@/services/db/pob';

type ModuleStatus = 'active' | 'coming_soon';

interface ModuleCard {
  key: string;
  name: string;
  description: string;
  icon: React.ElementType;
  status: ModuleStatus;
  href?: string;
  cta: string;
}

const ACTIVE_MODULES: ModuleCard[] = [
  {
    key: 'edsr',
    name: 'eDSR',
    description:
      'Electronic Daily Safety Report — field safety documentation for your operations',
    icon: ClipboardList,
    status: 'active',
    href: '/dashboard',
    cta: 'Open eDSR',
  },
  {
    key: 'pob',
    name: 'POB',
    description:
      'Personnel on board — daily manifest, hitch tracking, and SSE management for your operations',
    icon: Ship,
    status: 'active',
    href: '/pob',
    cta: 'Open POB',
  },
  {
    key: 'projo',
    name: 'PROJO',
    description:
      'Personnel & project management — certifications, assignments, and workforce tracking',
    icon: Users,
    status: 'coming_soon',
    cta: 'Coming Soon',
  },
  {
    key: 'analytics',
    name: 'Analytics',
    description:
      'Safety trend analysis, risk scoring, and predictive insights across your operations',
    icon: BarChart2,
    status: 'coming_soon',
    cta: 'Coming Soon',
  },
];

const FUTURE_MODULES: ModuleCard[] = [
  {
    key: 'lessons_learned',
    name: 'Lessons Learned Library',
    description:
      'Capture, search, and apply lessons learned across operations. AI-surfaced insights prevent repeat incidents.',
    icon: BookOpen,
    status: 'coming_soon',
    cta: 'Coming Soon',
  },
  {
    key: 'document_repository',
    name: 'Document Repository',
    description:
      'Policies, procedures, and bridging documents — searchable and linked to operational context.',
    icon: FolderOpen,
    status: 'coming_soon',
    cta: 'Coming Soon',
  },
  {
    key: 'compliance',
    name: 'Compliance & Regulatory',
    description:
      'SEMS/SEMP compliance tracking, audit readiness, and regulatory reporting.',
    icon: Shield,
    status: 'coming_soon',
    cta: 'Coming Soon',
  },
  {
    key: 'training',
    name: 'Training & Certification',
    description:
      'Certification tracking, competency records, and training effectiveness analytics.',
    icon: GraduationCap,
    status: 'coming_soon',
    cta: 'Coming Soon',
  },
  {
    key: 'audit',
    name: 'Audit & Inspection',
    description:
      'Structured audit workflows, inspection checklists, and finding management.',
    icon: ClipboardCheck,
    status: 'coming_soon',
    cta: 'Coming Soon',
  },
  {
    key: 'incident',
    name: 'Incident Management',
    description:
      'Full incident lifecycle — reporting, RCA workflows, corrective actions, and regulatory submissions.',
    icon: AlertOctagon,
    status: 'coming_soon',
    cta: 'Coming Soon',
  },
  {
    key: 'knowledge',
    name: 'Knowledge Management',
    description:
      'AI-powered knowledge base connecting operational data, lessons learned, and procedure libraries.',
    icon: Brain,
    status: 'coming_soon',
    cta: 'Coming Soon',
  },
];

function useFirstName() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ['platform_home_first_name', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const metaFirst = (user.user_metadata as { first_name?: string } | null)?.first_name;
      if (metaFirst) return metaFirst;
      const { data } = await supabase
        .from('users')
        .select('first_name')
        .eq('id', user.id)
        .maybeSingle();
      return data?.first_name ?? null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
}

function useTenantName() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['platform_home_tenant_name', tenantId],
    queryFn: async () => {
      const { data } = await supabase
        .from('tenants')
        .select('name')
        .eq('id', tenantId!)
        .single();
      return data?.name ?? null;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}

function usePOBLiveStat() {
  const projectId = useAuthStore((s) => s.projectId);
  return useQuery({
    queryKey: ['platform_home_pob_today', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const manifest = await getTodayPublishedManifest(projectId);
      if (!manifest) return { hasManifest: false, total: 0 };
      const { count } = await supabase
        .from('pob_entries')
        .select('*', { count: 'exact', head: true })
        .eq('manifest_id', manifest.id)
        .eq('is_deleted', false)
        .neq('status', 'departed');
      return { hasManifest: true, total: count ?? 0 };
    },
    enabled: !!projectId,
    staleTime: 60 * 1000,
  });
}

function ModuleCardView({
  module,
  liveStat,
}: {
  module: ModuleCard;
  liveStat?: string | null;
}) {
  const Icon = module.icon;
  const active = module.status === 'active';

  return (
    <Card
      className={`group flex h-full flex-col border transition-shadow ${
        active ? 'hover:shadow-md' : 'opacity-80'
      }`}
    >
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between">
          <div
            className={`flex h-11 w-11 items-center justify-center rounded-lg ${
              active
                ? 'bg-mozaic-teal/10 text-mozaic-teal'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            <Icon className="h-6 w-6" />
          </div>
          {active ? (
            <Badge className="bg-mozaic-teal/15 text-mozaic-teal hover:bg-mozaic-teal/15">
              Active
            </Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground">
              Coming Soon
            </Badge>
          )}
        </div>
        <div>
          <h3 className="text-xl font-semibold text-foreground">{module.name}</h3>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-between gap-4">
        <div className="space-y-2">
          <p className="text-sm leading-relaxed text-muted-foreground">
            {module.description}
          </p>
          {liveStat && (
            <p className="text-sm font-medium text-mozaic-teal">{liveStat}</p>
          )}
        </div>
        {active && module.href ? (
          <Link
            to={module.href}
            className="inline-flex h-10 w-full items-center justify-center rounded-md bg-mozaic-teal px-4 text-sm font-medium text-white shadow transition-colors hover:bg-mozaic-teal/90"
          >
            {module.cta}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        ) : (
          <Button disabled className="w-full" variant="secondary">
            {module.cta}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function PlatformHomePage() {
  const user = useAuthStore((s) => s.user);
  const firstName = useFirstName();
  const tenantName = useTenantName();

  const greetingName = useMemo(() => {
    if (firstName.data) return firstName.data;
    const email = user?.email ?? '';
    if (!email) return 'there';
    return email.split('@')[0];
  }, [firstName.data, user?.email]);

  const today = useMemo(() => format(new Date(), "EEEE, MMMM d, yyyy"), []);

  const pobStat = usePOBLiveStat();
  const pobLiveStat = useMemo<string | null>(() => {
    if (!pobStat.data) return null;
    if (pobStat.data.hasManifest) return `Today's POB: ${pobStat.data.total} on board`;
    return 'No published manifest today';
  }, [pobStat.data]);

  return (
    <div className="mx-auto max-w-6xl">
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Welcome back, {greetingName}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {tenantName.data ?? 'Your Organization'}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">{today}</p>
      </header>

      {/* Active modules */}
      <section>
        <h2 className="mb-4 text-lg font-semibold text-foreground">Your Modules</h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {ACTIVE_MODULES.map((m) => (
            <ModuleCardView
              key={m.key}
              module={m}
              liveStat={m.key === 'pob' ? pobLiveStat : null}
            />
          ))}
        </div>
      </section>

      {/* Coming Soon — future modules */}
      <section className="mt-12">
        <h2 className="text-lg font-semibold text-foreground">Coming Soon</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The MOZAIC platform grows with your operation.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FUTURE_MODULES.map((m) => (
            <ModuleCardView key={m.key} module={m} />
          ))}
        </div>

        <p className="mt-8 text-xs leading-relaxed text-muted-foreground">
          Additional modules are provisioned by your MOZAIC administrator.
          Contact{' '}
          <a
            href="mailto:support@mozaicplatform.com"
            className="text-mozaic-teal hover:underline"
          >
            support@mozaicplatform.com
          </a>{' '}
          to enable new modules for your organization.
        </p>
      </section>
    </div>
  );
}
